import * as Sentry from '@sentry/react'
import type { Breadcrumb, ErrorEvent } from '@sentry/react'

// Hardened Sentry scrubber (inspection WEB-M11 / LIFF-H4) — this file is
// MIRRORED from web/src/lib/sentryUtils.ts; keep both in sync.
//
// The old scrubber covered request.data/extra only and only at the top
// level. Axios error messages and breadcrumbs can embed `oauth/token` URLs
// carrying refresh_token=, auth codes, and e-sign capability tokens — so we
// also scrub exception messages, breadcrumbs (message + data, recursively),
// and sensitive substrings inside any string we touch.

const SCRUB_KEYS = new Set([
  'password',
  'password_confirmation',
  'token',
  'access_token',
  'refresh_token',
  'client_secret',
  'auth_code',
  'lease_code',
  'credit_card',
  'card_number',
  'cvv',
  'tax_id',
  'national_id',
  'id_card_number',
  'bank_account',
  'phone',
  'address',
  'email',
  'line_user_id',
  'slip_image',
  'slip_url',
  'trans_ref',
  'authorization',
  'cookie',
])

// Sensitive values embedded INSIDE strings (axios error messages, breadcrumb
// URLs, exception messages):
//   1. query/form params: ?refresh_token=..., &auth_code=... etc.
//   2. JSON-encoded bodies: "refresh_token":"..."
//   3. e-sign capability tokens in URL paths: /sign/<token>
const QUERY_PARAM_PATTERN =
  /([?&](?:refresh_token|access_token|token|client_secret|auth_code|lease_code|code|otp)=)[^&\s'"]+/gi
const JSON_FIELD_PATTERN =
  /("(?:refresh_token|access_token|token|client_secret|auth_code|lease_code|password|otp)"\s*:\s*")[^"]*(?=")/gi
const SIGN_PATH_PATTERN = /(\/sign\/)[^/?#\s'"]+/gi

/** Scrub sensitive substrings out of a free-text string. Exported for tests. */
export function scrubString(value: string): string {
  return value
    .replace(QUERY_PARAM_PATTERN, '$1[FILTERED]')
    .replace(JSON_FIELD_PATTERN, '$1[FILTERED]')
    .replace(SIGN_PATH_PATTERN, '$1[FILTERED]')
}

/** Recursively scrub an object in place: sensitive keys are replaced
 *  wholesale; string values are pattern-scrubbed. */
function scrubObject(obj: Record<string, unknown>, depth = 0): void {
  if (depth > 6) return // defensive: no unbounded recursion
  for (const key of Object.keys(obj)) {
    const value = obj[key]
    if (SCRUB_KEYS.has(key.toLowerCase())) {
      obj[key] = '[FILTERED]'
    } else if (typeof value === 'string') {
      obj[key] = scrubString(value)
    } else if (Array.isArray(value)) {
      obj[key] = value.map((v) => {
        if (typeof v === 'string') return scrubString(v)
        if (v && typeof v === 'object') scrubObject(v as Record<string, unknown>, depth + 1)
        return v
      })
    } else if (value && typeof value === 'object') {
      scrubObject(value as Record<string, unknown>, depth + 1)
    }
  }
}

/**
 * Scrub a single breadcrumb. Wire as Sentry.init `beforeBreadcrumb` so
 * sensitive values never even enter the breadcrumb buffer (axios request
 * URLs/bodies, console lines).
 */
export function scrubSentryBreadcrumb(breadcrumb: Breadcrumb): Breadcrumb {
  if (typeof breadcrumb.message === 'string') {
    breadcrumb.message = scrubString(breadcrumb.message)
  }
  if (breadcrumb.data && typeof breadcrumb.data === 'object') {
    scrubObject(breadcrumb.data as Record<string, unknown>)
  }
  return breadcrumb
}

/**
 * Scrubs sensitive values from a Sentry error event before it is sent:
 * request data/url, extra, breadcrumbs, and exception messages.
 */
export function scrubSentryEvent(event: ErrorEvent): ErrorEvent {
  if (event.request?.data && typeof event.request.data === 'object') {
    scrubObject(event.request.data as Record<string, unknown>)
  }
  if (typeof event.request?.url === 'string') {
    event.request.url = scrubString(event.request.url)
  }
  if (typeof event.request?.query_string === 'string') {
    event.request.query_string = scrubString(event.request.query_string)
  }

  if (event.extra && typeof event.extra === 'object') {
    scrubObject(event.extra as Record<string, unknown>)
  }

  // Breadcrumbs attached to the event (backstop — beforeBreadcrumb already
  // scrubs at creation, but events from other sources may carry their own).
  if (Array.isArray(event.breadcrumbs)) {
    for (const crumb of event.breadcrumbs) {
      scrubSentryBreadcrumb(crumb)
    }
  }

  // Exception messages — axios errors embed the failing URL (and with it
  // any token query params) into the error message itself.
  for (const exception of event.exception?.values ?? []) {
    if (typeof exception.value === 'string') {
      exception.value = scrubString(exception.value)
    }
  }

  return event
}

/**
 * EP-160 T160-102l: tag every later event with the account it came from, so
 * an account's errors can be found in Sentry. Account id and kind are C0
 * (operational) data. There is deliberately no Sentry user: LIFF has no
 * analytics distinct id and a tenant id is not needed to find an account's
 * errors. `kind` is tagged only when the payload carries it (a white-label
 * build's bundled snapshot may predate the key).
 */
export function tagSentryAccount(account: { id: number; kind?: string }): void {
  const tags: Record<string, string> = { account_id: String(account.id), role: 'tenant' }
  if (account.kind) tags.account_kind = account.kind
  Sentry.setTags(tags)
}
